import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { take, takeUntil } from 'rxjs/operators';
import { PermissionsService } from 'src/lib/amg-authorization/permissions.service';
import { OpsAuthorizationService } from 'src/lib/tcs-authorization/ops-authorization.service';
import { OrgAuthorizationService } from 'src/lib/tcs-authorization/org-authorization.service';
import { AuthenticationService } from 'src/lib/tcs-identity/authentication.service';
import { getCompanyCodeFromRoute, getUrl } from 'src/util/helpers/url-helper';
import { Observable, Subject } from 'rxjs';
import { firstValueOrDefault, firstValueOrUndefined } from 'src/util/rxjs';
import { UserRole } from 'src/graphql/generated/graphql.generated';
import { OosAuthorizationService } from 'src/lib/tcs-authorization/oos-authorization.service';
import { InsightsAuthorizationService } from 'src/lib/tcs-authorization/insights-authorization.service';
import { RouteAuthorizationService } from 'src/lib/tcs-authorization/route-authorization.service';
import {
  CanActivateChildPermissions,
  CanActivatePermissions,
  collectAuthorizations,
  getAuthorizationStrategies,
  RouteAuthorizationStatus,
} from 'src/lib/tcs-authorization/route-authorization-strategies';
import { UserService } from '../../../lib/tcs-identity/user.service';
import { Logger } from '../tcs-logging/logger';
import { TcsPermission } from './permissions';

export function grantAccess(permissions: TcsPermission[]): CanActivatePermissions {
  return {
    grantingPermissions: permissions,
  };
}

export function grantAccessDeep(permissions: TcsPermission[]): CanActivateChildPermissions {
  return {
    grantingPermissionsDeep: permissions,
  };
}

function getDestinationRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  let routeOrChild: ActivatedRouteSnapshot | null = route;
  while (routeOrChild.firstChild != null) {
    routeOrChild = routeOrChild.firstChild;
  }
  return routeOrChild;
}

const USE_STRICT_AUTHORIZATION = true;

@Injectable({ providedIn: 'root' })
export class AuthorizationGuard implements OnDestroy {
  private _onDestroy$ = new Subject();

  constructor(
    private _authUser: UserService,
    private _router: Router,
    private _logger: Logger,
    private _permissions: PermissionsService,
    private _auth: AuthenticationService,
    private _opsAuthz: OpsAuthorizationService,
    private _orgAuthz: OrgAuthorizationService,
    private _oosAuthz: OosAuthorizationService,
    private _insightsAuthz: InsightsAuthorizationService,
    private _routeAuthz: RouteAuthorizationService,
  ) {}

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }

  public async canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const authenticated = await this._auth.isAuthenticated();
    if (!authenticated) {
      await this._auth.login({ caller: 'AuthorizationGuard.canActivate', fromUri: state.url });
      return false;
    }

    return this._authorize(next, state);
  }

  public async canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const authenticated = await this._auth.isAuthenticated();
    if (!authenticated) {
      await this._auth.login({ caller: 'AuthorizationGuard.canActivateChild', fromUri: state.url });
      return false;
    }
    const destinationRoute = getDestinationRoute(childRoute);
    return this._authorize(destinationRoute, state);
  }

  private async _authorize(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (USE_STRICT_AUTHORIZATION) {
      return this._authorizeStrict(route, state);
    } else {
      return this._authorizeNormal(route, state);
    }
  }

  private async _authorizeStrict(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const authStatus: RouteAuthorizationStatus = await this._routeAuthz.applyGuard(route);

    switch (authStatus) {
      case RouteAuthorizationStatus.Authorized:
        return true;
      case RouteAuthorizationStatus.NoRolesGranted:
      case RouteAuthorizationStatus.NoAccessGranted:
        const requestedUrl = getUrl(route);
        this._logger.warning('unauthorized navigation - ' + authStatus, {
          currentUrl: state.url,
          requestedUrl,
          error: authStatus.toString(),
        });
        this._router.navigate(['/unauthorized'], {
          queryParams: {
            requestedUrl,
            error: authStatus,
          },
          queryParamsHandling: 'merge',
        });
        return false;
      case RouteAuthorizationStatus.Unauthorized:
        const user = await firstValueOrUndefined(this._authUser.user$.pipe(take(1)));
        const roles = user?.userRolesAllScopes ?? [];
        const permissions = collectAuthorizations(route);
        const strategies = getAuthorizationStrategies(route);
        return this._unauthorized(state, getUrl(route), strategies, roles, permissions);
    }
  }

  private async _authorizeNormal(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const requestedUrl = getUrl(route);
    const user = await firstValueOrUndefined(
      this._authUser.user$.pipe(take(1), takeUntil(this._onDestroy$)),
    );
    let accessError = '';

    if (user == null || user.grantTcsAccess !== true) {
      accessError = 'tcsAccessDenied';
    }

    const roles = user?.userRolesAllScopes ?? [];
    const permissions = collectAuthorizations(route);

    if (!accessError && roles.length === 0 && user?.allCommunicationCentersAccessible !== true) {
      accessError = 'noRolesGranted';
    }

    if (accessError) {
      this._logger.warning('unauthorized navigation - ' + accessError, {
        currentUrl: state.url,
        requestedUrl,
        error: accessError,
      });

      this._router.navigate(['/unauthorized'], {
        queryParams: {
          requestedUrl,
          error: accessError,
        },
        queryParamsHandling: 'merge',
      });
      return false;
    }

    if (this._permissions.hasAnyPermission(roles, permissions)) {
      return true;
    }

    const strategies = getAuthorizationStrategies(route);

    for (const strategy of strategies) {
      if (
        strategy === 'ops' ||
        strategy === 'org' ||
        strategy === 'oos-rum' ||
        strategy === 'insights'
      ) {
        const companyCode = getCompanyCodeFromRoute(route);

        if (companyCode != null && strategy === 'ops') {
          const isAuthorized = await this._isTrue(
            this._opsAuthz.isAuthorizedForRoute(route, companyCode),
          );
          if (!isAuthorized) {
            return this._unauthorized(state, requestedUrl, strategies, roles, permissions);
          }
        }

        if (companyCode != null && strategy === 'org') {
          const isAuthorized = await this._isTrue(
            this._orgAuthz.isAuthorizedForCompanyCode(companyCode),
          );
          if (!isAuthorized) {
            return this._unauthorized(state, requestedUrl, strategies, roles, permissions);
          }
        }

        if (companyCode != null && strategy === 'oos-rum') {
          const isAuthorized = await this._isTrue(
            this._oosAuthz.isAuthorizedForRoute(route, companyCode),
          );
          if (!isAuthorized) {
            return this._unauthorized(state, requestedUrl, strategies, roles, permissions);
          }
        }

        if (companyCode != null && strategy === 'insights') {
          const isAuthorized = await this._isTrue(
            this._insightsAuthz.isAuthorizedForCompanyCode(companyCode),
          );
          if (!isAuthorized) {
            return this._unauthorized(state, requestedUrl, strategies, roles, permissions);
          }
        }
      }

      if (strategy === 'fleet-status') {
        const userRolesAllScopes = user?.userRolesAllScopes ?? [];
        if (this._permissions.hasAnyPermission(userRolesAllScopes, permissions)) {
          return true;
        }
      }

      if (strategy === 'rtt') {
        const userRolesAllScopes = user?.userRolesAllScopes ?? [];
        if (this._permissions.hasAnyPermission(userRolesAllScopes, permissions)) {
          return true;
        }
      }

      if (strategy === 'support') {
        const userRolesAllScopes = user?.userRolesAllScopes ?? [];
        if (this._permissions.hasAnyPermission(userRolesAllScopes, permissions)) {
          return true;
        }
      }
    }

    // if (this._permissions.hasAnyPermission(roles, permissions)) {
    //   return true;
    // }

    return this._unauthorized(state, requestedUrl, strategies, roles, permissions);
  }

  private async _isTrue(obs: Observable<boolean>): Promise<boolean> {
    return await firstValueOrDefault(obs.pipe(take(1), takeUntil(this._onDestroy$)), false);
  }

  private _unauthorized(
    state: RouterStateSnapshot,
    requestedUrl: string,
    strategies: string[],
    roles: UserRole[],
    permissions: string[],
  ) {
    this._logger.warning('unauthorized navigation', {
      currentUserRoles: roles.join(', '),
      grantingPermissions: permissions.join(', '),
      strategies: strategies.join(', '),
      currentUrl: state.url,
      requestedUrl,
    });
    this._router.navigate(['/unauthorized'], {
      queryParams: {
        requestedUrl,
      },
    });
    return false;
  }
}
